import { FC, useEffect, useState } from 'react';
import { useCategories, useLanguage } from '@/contexts';
import { getLocalizedCollectionName, getLocalizedText } from '@/utils/localizeTexts';
import { AdaptiveUsageWidget, UsageWidgetErrorBoundary } from '@/components/UsageWidget';

interface Collection {
  id: number;
  name: string;
  name_es?: string;
  is_private: boolean;
}

interface Props {
  isLoggedIn: boolean;
  collections: Collection[];
  selectedCollectionId: string | null;
  onSelectCollection: (id: string | null) => void;
  onShowCreateCollectionModal: () => void;
  selectedCategory: string | null;
  onSelectCategory: (category: string) => void;
  onSelectMyPrompts?: () => void;
  showMyPrompts?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  publicMode?: boolean;
}

export const Sidebar: FC<Props> = ({
  isLoggedIn,
  collections,
  selectedCollectionId,
  onSelectCollection,
  onShowCreateCollectionModal,
  selectedCategory,
  onSelectCategory,
  onSelectMyPrompts,
  showMyPrompts,
  isOpen = false,
  onClose,
  publicMode = false
}) => {
  const { t, i18n } = useLanguage();
  const { categories, loading } = useCategories();
  // Create a new array with 'All' category at the beginning instead of mutating the original
  const categoriesWithAll = [{ id: 'All', name: 'All' }, ...categories];
  
  // Get usage widget variant from localStorage
  const [widgetVariant, setWidgetVariant] = useState<'badge' | 'footer' | 'floating' | 'icon'>(() => {
    const stored = localStorage.getItem('usage_widget_variant');
    return (stored as 'badge' | 'footer' | 'floating' | 'icon') || 'badge';
  });

  // Listen for changes to widget variant preference
  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('usage_widget_variant');
      const newVariant = (stored as 'badge' | 'footer' | 'floating' | 'icon') || 'badge';
      setWidgetVariant(newVariant);
    };

    // Listen for custom event (same window) and storage event (other windows/tabs)
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('widgetVariantChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('widgetVariantChanged', handleStorageChange);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-electric-blue"></div>
      </div>
    );
  }

  return (
    <>

      {/* Sidebar container */}
      <div
        className={`fixed top-0 left-0 w-[85vw] sm:w-80 h-full overflow-y-auto lg:sticky lg:top-24 lg:left-auto lg:w-full lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto bg-white dark:bg-slate-900 shadow lg:shadow-md dark:shadow-lg dark:shadow-slate-950 px-4 sm:px-6 py-4 border border-gray-300 dark:border-slate-700 rounded-lg z-40 transition-transform duration-300 transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        aria-label="Prompt Categories and Collections"
      >
        {/* Hamburger button (Mobile only) */}
        <button
          className="lg:hidden absolute right-3 sm:right-4 top-3 sm:top-4 z-50 bg-gray-100 dark:bg-slate-800 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-slate-700"
          onClick={onClose}
        >
          ✕
        </button>
        <div className="p-3 sm:p-4 lg:p-0 pt-12 sm:pt-16 lg:pt-0">
          <h3 className="font-semibold text-base sm:text-lg mb-3 text-start text-gray-900 dark:text-slate-100">{t("categories")}</h3>
          <ul className="space-y-2 mb-6 sm:mb-8">
            {categoriesWithAll.map(({ id, name, name_es, prompt_count }) => (
              <li key={id}>
                <button
                  onClick={() => {
                    onSelectCategory(name);
                    // Close mobile sidebar after selection
                    if (onClose) onClose();
                  }}
                  className={`block w-full text-left text-sm sm:text-base text-gray-600 dark:text-slate-400 hover:text-brand-electric-blue dark:hover:text-blue-400 transition-colors duration-200 rounded-md px-2 py-1 ${selectedCategory === name ? 'font-bold text-brand-electric-blue bg-brand-light-gray dark:bg-slate-800 dark:text-blue-400' : ''}`}
                >
                  <span className="flex justify-between items-center">
                    <span>{getLocalizedText({ name, name_es }, i18n.language)}</span>
                    {prompt_count !== undefined && (
                      <span className="text-xs text-gray-400 dark:text-slate-500 bg-gray-100 dark:bg-slate-800 px-2 py-1 rounded-full">
                        {prompt_count}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {/* Prompt Collections/Folders */}
          {!publicMode && isLoggedIn && (
            <div className="mt-6 sm:mt-8">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-base sm:text-lg text-start text-gray-900 dark:text-slate-100">{t("myCollections")}</h3>
                <button
                  onClick={onShowCreateCollectionModal}
                  className="text-brand-electric-blue dark:text-blue-400 hover:text-brand-dark-blue dark:hover:text-blue-300 text-sm font-medium"
                >
                  + {t("new")}
                </button>
              </div>
              {collections.length > 0 ? (
                <ul className="space-y-2">
                  {collections.map((collection) => (
                    <li key={collection.id}>
                      <button
                        onClick={() => onSelectCollection(collection.id.toString())}
                        className={`block w-full text-left text-sm sm:text-base text-gray-600 dark:text-slate-400 hover:text-brand-electric-blue dark:hover:text-blue-400 transition-colors duration-200 rounded-md px-2 py-1 ${selectedCollectionId === collection.id.toString() ? 'font-bold text-brand-electric-blue bg-brand-light-gray dark:bg-slate-800 dark:text-blue-400' : ''
                          }`}
                      >
                        {getLocalizedCollectionName(collection, i18n.language)}
                      </button>
                    </li>
                  ))}
                  <li>
                    <button
                      onClick={() => onSelectMyPrompts && onSelectMyPrompts()}
                      className={`block w-full text-left text-sm sm:text-base text-gray-600 dark:text-slate-400 hover:text-brand-electric-blue dark:hover:text-blue-400 transition-colors duration-200 rounded-md px-2 py-1 ${showMyPrompts ? 'font-bold text-brand-electric-blue bg-brand-light-gray dark:bg-slate-800 dark:text-blue-400' : ''
                        }`}
                    >
                      {t('allMyPrompts')}
                    </button>
                  </li>
                </ul>
              ) : (
                <p className="text-sm text-gray-500 dark:text-slate-400">{t("noCollections")}</p>
              )}
            </div>
          )}

          {/* Usage Widget - Only show in sidebar when variant is 'badge' */}
          {!publicMode && isLoggedIn && widgetVariant === 'badge' && (
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-slate-700">
              <UsageWidgetErrorBoundary>
                <AdaptiveUsageWidget variant="badge" />
              </UsageWidgetErrorBoundary>
            </div>
          )}
        </div>
      </div>

      {/* Backdrop (only for mobiles when opened */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-30 lg:hidden"
          onClick={onClose}
        />
      )}
    </>
  );
};
